<template>
  <main class="login-page">
    <header class="login-header">
      <div class="login-brand">
        <img src="../assets/hotpizza/pizzaking-logo.svg" alt="PizzaKing" />
      </div>
      <button type="button" class="login-back" @click="openHomeAuth('register')">
        注册账号 <span></span>
      </button>
    </header>

    <section class="login-stage">
      <div class="login-showcase" aria-hidden="true">
        <div class="login-orbit orbit-one"></div>
        <div class="login-orbit orbit-two"></div>
        <div class="login-pizza-mark"><span></span></div>
        <p>OPEN · COLLECT · ENJOY</p>
        <h1>欢迎回到<br />PizzaKing</h1>
        <small>登录后继续打开箱子、管理背包与查看账户记录</small>
      </div>

      <form class="login-card" @submit.prevent="submitLogin">
        <div class="login-card-kicker">PLAYER ACCESS</div>
        <h2>登录账号</h2>
        <p class="login-card-copy">使用已注册手机号登录</p>

        <div class="login-tabs" role="tablist" aria-label="登录方式">
          <button
            type="button"
            :class="{ active: loginMode === 'password' }"
            @click="switchMode('password')"
          >
            密码登录
          </button>
          <button
            type="button"
            :class="{ active: loginMode === 'code' }"
            @click="switchMode('code')"
          >
            验证码登录
          </button>
        </div>

        <div v-if="errorText" class="login-error">{{ errorText }}</div>

        <label class="login-field">
          <span>手机号码</span>
          <input v-model.trim="account" type="tel" inputmode="numeric" autocomplete="username" placeholder="请输入手机号码" />
        </label>

        <label v-if="loginMode === 'password'" class="login-field">
          <span>登录密码</span>
          <div class="login-password">
            <input
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              placeholder="请输入登录密码"
            />
            <button type="button" @click="showPassword = !showPassword">{{ showPassword ? "隐藏" : "显示" }}</button>
          </div>
        </label>

        <label v-else class="login-field">
          <span>短信验证码</span>
          <div class="login-code">
            <input v-model.trim="smsCode" type="text" inputmode="numeric" autocomplete="one-time-code" placeholder="请输入验证码" />
            <button type="button" :disabled="codeSeconds > 0 || sendingCode" @click="openCaptcha">
              {{ codeButtonText }}
            </button>
          </div>
        </label>

        <div class="login-options">
          <label><input v-model="remember" type="checkbox" />记住登录</label>
          <button type="button" @click="openHomeAuth('forgot')">忘记密码</button>
        </div>

        <div class="login-consent">
          <label><input v-model="adultAccepted" type="checkbox" />本人已年满18周岁</label>
          <label>
            <input v-model="termsAccepted" type="checkbox" />
            我已阅读并接受
            <button type="button" @click="openPolicy('agreement')">用户协议</button>
            和
            <button type="button" @click="openPolicy('privacy')">隐私条款</button>
          </label>
        </div>

        <button class="login-submit" type="submit" :disabled="submitting">
          <span>{{ submitting ? "正在登录" : "登录" }}</span><i></i>
        </button>

        <div class="login-register">
          还没有账号？<button type="button" @click="openHomeAuth('register')">注册账号</button>
        </div>
      </form>
    </section>

    <div v-if="captchaVisible" class="captcha-mask" @click.self="closeCaptcha">
      <section class="captcha-card">
        <button type="button" class="captcha-close" aria-label="关闭" @click="closeCaptcha">×</button>
        <span>安全验证</span>
        <h3>请输入图形验证码</h3>
        <p>点击验证码图片可以更换</p>
        <div class="captcha-row">
          <input v-model.trim="captchaInput" type="text" autocomplete="off" maxlength="4" @keyup.enter="confirmCaptcha" />
          <button type="button" class="captcha-image" title="点击切换验证码" @click="refreshCaptcha">
            <SIdentify :identify-code="captchaCode" />
          </button>
        </div>
        <button type="button" class="captcha-confirm" @click="confirmCaptcha">确认并发送</button>
      </section>
    </div>

    <div v-if="policyKey" class="login-policy-mask" @click.self="closePolicy">
      <section class="login-policy-card">
        <header>
          <div>
            <span>PizzaKing-CJ</span>
            <h3>{{ policyPage ? policyPage.title : "正在加载" }}</h3>
          </div>
          <button type="button" aria-label="关闭" @click="closePolicy">×</button>
        </header>
        <div v-if="policyPage" class="login-policy-content">
          <p>{{ policyPage.lead }}</p>
          <article v-for="(section, index) in policyPage.sections" :key="section.title">
            <b>{{ index + 1 }}</b>
            <div>
              <h4>{{ section.title }}</h4>
              <p>{{ section.text }}</p>
            </div>
          </article>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import { SIdentify } from "./common";
import { EXPIRE_TIME } from "@/config";
import { loadSiteContent } from "@/services/siteContent";

export default {
  name: "LoginPage",
  components: { SIdentify },
  data() {
    return {
      loginMode: "password",
      account: localStorage.getItem("csgoNum") || "",
      password: "",
      smsCode: "",
      showPassword: false,
      remember: true,
      adultAccepted: false,
      termsAccepted: false,
      submitting: false,
      sendingCode: false,
      codeSeconds: 0,
      codeTimer: null,
      errorText: "",
      captchaVisible: false,
      captchaInput: "",
      captchaCode: "",
      captchaChars: "1234567890abcdefghijklmnopqrstuvwxyz".split(""),
      policyKey: "",
      siteContent: null,
    };
  },
  computed: {
    codeButtonText() {
      if (this.sendingCode) return "发送中";
      return this.codeSeconds > 0 ? `${this.codeSeconds} s` : "获取验证码";
    },
    policyPage() {
      return this.siteContent && this.policyKey ? this.siteContent[this.policyKey] : null;
    },
  },
  created() {
    this.refreshCaptcha();
    if (["agreement", "privacy"].includes(this.$route.query.policy)) {
      this.openPolicy(this.$route.query.policy);
    }
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
    if (userInfo && userInfo.token && userInfo.expire_time > new Date().getTime()) {
      this.goAfterLogin();
    }
  },
  beforeDestroy() {
    clearInterval(this.codeTimer);
  },
  methods: {
    openHomeAuth(auth) {
      this.$router.push({ path: "/Index", query: { auth } });
    },
    openPolicy(key) {
      this.policyKey = key;
      loadSiteContent(this.$axios).then((content) => {
        this.siteContent = content;
      });
    },
    closePolicy() {
      this.policyKey = "";
    },
    switchMode(mode) {
      this.loginMode = mode;
      this.errorText = "";
    },
    openCaptcha() {
      if (!this.account) {
        this.showError("请输入手机号码");
        return;
      }
      this.captchaInput = "";
      this.refreshCaptcha();
      this.captchaVisible = true;
    },
    closeCaptcha() {
      this.captchaVisible = false;
      this.captchaInput = "";
    },
    refreshCaptcha() {
      this.captchaCode = "";
      for (let index = 0; index < 4; index += 1) {
        this.captchaCode += this.captchaChars[Math.floor(Math.random() * this.captchaChars.length)];
      }
    },
    confirmCaptcha() {
      if (this.captchaInput.toLowerCase() !== this.captchaCode.toLowerCase()) {
        this.showError("图形验证码错误");
        this.captchaInput = "";
        this.refreshCaptcha();
        return;
      }
      this.captchaVisible = false;
      this.sendLoginCode();
    },
    sendLoginCode() {
      this.sendingCode = true;
      this.errorText = "";
      this.$axios
        .post("/index/Register/getloginCodenew", this.$qs.stringify({ account: this.account }))
        .then((res) => {
          if (res.data.status !== 1) {
            this.showError(res.data.msg);
            return;
          }
          this.$message.success("已发送验证码，请注意查收");
          this.codeSeconds = 60;
          this.codeTimer = setInterval(() => {
            this.codeSeconds -= 1;
            if (this.codeSeconds <= 0) clearInterval(this.codeTimer);
          }, 1000);
        })
        .catch((error) => {
          const data = error.data || (error.response && error.response.data);
          this.showError((data && data.msg) || "验证码发送失败");
        })
        .finally(() => {
          this.sendingCode = false;
        });
    },
    submitLogin() {
      if (!this.adultAccepted || !this.termsAccepted) {
        this.showError("请确认年龄并阅读用户协议和隐私条款");
        return;
      }
      if (!this.account || (this.loginMode === "password" ? !this.password : !this.smsCode)) {
        this.showError(this.loginMode === "password" ? "请输入手机号码和密码" : "请输入手机号码和验证码");
        return;
      }

      const endpoint = this.loginMode === "password" ? "/index/Login/loginnew" : "/index/Register/checkloginCodenew";
      const payload = this.loginMode === "password"
        ? { account: this.account, password: this.password }
        : { account: this.account, code: this.smsCode };

      this.submitting = true;
      this.errorText = "";
      this.$axios
        .post(endpoint, this.$qs.stringify(payload))
        .then((res) => {
          if (res.data.status !== 1) {
            this.showError(res.data.msg || (this.loginMode === "password" ? "账号或密码错误" : "验证码错误"));
            return;
          }
          this.persistLogin(res.data.data);
          this.$message.success("登录成功");
          this.goAfterLogin();
        })
        .catch((error) => {
          const data = error.data || (error.response && error.response.data);
          this.showError((data && data.msg) || (this.loginMode === "password" ? "账号或密码错误" : "验证码错误"));
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    persistLogin(data) {
      const userInfo = {
        ...data,
        expire_time: new Date().getTime() + EXPIRE_TIME,
      };
      if (this.remember) {
        localStorage.setItem("csgoNum", this.account);
      } else {
        localStorage.removeItem("csgoNum");
      }
      localStorage.removeItem("csgoPass");
      localStorage.setItem("userInfo", JSON.stringify(userInfo));
      this.$store.commit("setUserToken", data.token);
      this.$store.commit("setMobile", data.mobile);
      this.$store.commit("getId", data);
      this.$store.commit("getLogin", false);
    },
    goAfterLogin() {
      const redirect = typeof this.$route.query.redirect === "string" ? this.$route.query.redirect : "/Index";
      this.$router.replace(redirect);
    },
    showError(message) {
      this.errorText = message || "操作失败，请稍后重试";
    },
  },
};
</script>

<style scoped>
.login-page {
  --login-bg: #171923;
  --login-panel: #242632;
  --login-line: #3c3f50;
  --login-gold: #ffb328;
  min-height: 100vh;
  overflow: hidden;
  color: #f6f4ed;
  background:
    radial-gradient(circle at 22% 48%, rgba(255, 123, 20, 0.2), transparent 27%),
    radial-gradient(circle at 84% 20%, rgba(73, 116, 255, 0.13), transparent 26%),
    linear-gradient(135deg, #13151e, var(--login-bg) 52%, #1d202c);
  font-family: "Trebuchet MS", "Microsoft YaHei", sans-serif;
}

.login-header {
  height: 86px;
  padding: 0 46px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(17, 19, 28, 0.72);
}

.login-brand {
  border: 0;
  background: none;
}

.login-back {
  border: 0;
  background: none;
  cursor: pointer;
}

.login-brand img {
  display: block;
  width: 228px;
}

.login-back {
  height: 42px;
  padding: 0 18px;
  color: #f5e7c5;
  font-size: 15px;
  font-weight: 900;
  border: 1px solid rgba(255, 211, 122, 0.55);
  border-radius: 6px;
}

.login-back span {
  display: inline-block;
  width: 9px;
  height: 9px;
  margin-left: 10px;
  border-top: 2px solid currentColor;
  border-right: 2px solid currentColor;
  transform: rotate(45deg);
}

.login-stage {
  width: min(1180px, calc(100% - 48px));
  min-height: calc(100vh - 86px);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 470px;
  align-items: center;
  gap: 82px;
  padding: 48px 0;
  box-sizing: border-box;
}

.login-showcase {
  min-height: 590px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (min-width: 1400px) {
  .login-showcase {
    transform: translateX(-90px);
  }
}

@media (min-width: 981px) and (max-width: 1399px) {
  .login-showcase {
    transform: translateX(-36px);
  }
}

.login-showcase::before {
  content: "";
  width: 410px;
  height: 410px;
  position: absolute;
  left: 2px;
  top: 50%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 172, 46, 0.26), rgba(255, 110, 15, 0.05) 50%, transparent 70%);
  transform: translateY(-50%);
  filter: blur(2px);
}

.login-orbit {
  position: absolute;
  left: 36px;
  top: 50%;
  width: 330px;
  height: 330px;
  border: 1px solid rgba(255, 179, 40, 0.28);
  border-radius: 50%;
  transform: translateY(-50%);
}

.orbit-one::after,
.orbit-two::after {
  content: "";
  width: 9px;
  height: 9px;
  position: absolute;
  border-radius: 50%;
  background: var(--login-gold);
  box-shadow: 0 0 18px 5px rgba(255, 153, 22, 0.65);
}

.orbit-one::after { left: 28px; top: 38px; }
.orbit-two { width: 260px; height: 260px; left: 72px; border-style: dashed; transform: translateY(-50%) rotate(32deg); }
.orbit-two::after { right: 6px; bottom: 62px; }

.login-pizza-mark {
  width: 132px;
  height: 132px;
  position: absolute;
  left: 135px;
  top: 50%;
  border-radius: 28px;
  background: linear-gradient(135deg, #ffbd27, #ff5d0a);
  transform: translateY(-50%) rotate(45deg);
  box-shadow: 0 28px 70px rgba(255, 99, 6, 0.42);
}

.login-pizza-mark::before {
  content: "";
  position: absolute;
  inset: 22px;
  border: 5px solid #fff1c4;
  border-radius: 50% 50% 50% 16%;
}

.login-pizza-mark span,
.login-pizza-mark span::before,
.login-pizza-mark span::after {
  width: 14px;
  height: 14px;
  position: absolute;
  border-radius: 50%;
  background: #e73825;
}

.login-pizza-mark span { left: 50px; top: 38px; }
.login-pizza-mark span::before { content: ""; left: 28px; top: 24px; }
.login-pizza-mark span::after { content: ""; left: 2px; top: 48px; }

.login-showcase p,
.login-showcase h1,
.login-showcase small {
  width: 330px;
  position: relative;
  z-index: 2;
  margin-left: 390px;
}

.login-showcase p {
  margin-top: 0;
  margin-bottom: 18px;
  color: var(--login-gold);
  font-size: 12px;
  font-weight: 1000;
  letter-spacing: 0.2em;
}

.login-showcase h1 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: clamp(42px, 4vw, 70px);
  line-height: 0.98;
  letter-spacing: -0.04em;
}

.login-showcase small {
  color: #aeb2c1;
  font-size: 14px;
  line-height: 1.8;
}

.login-card {
  padding: 38px 42px 34px;
  position: relative;
  border: 1px solid var(--login-line);
  border-radius: 12px;
  background: linear-gradient(155deg, rgba(45, 48, 63, 0.98), rgba(30, 32, 44, 0.98));
  box-shadow: 0 35px 90px rgba(0, 0, 0, 0.42);
}

.login-card::before {
  content: "";
  height: 3px;
  position: absolute;
  left: 42px;
  right: 42px;
  top: 0;
  background: linear-gradient(90deg, transparent, var(--login-gold), #ff6c10, transparent);
  box-shadow: 0 0 18px rgba(255, 139, 21, 0.8);
}

.login-card-kicker { color: var(--login-gold); font-size: 11px; font-weight: 1000; letter-spacing: 0.18em; }
.login-card h2 { margin: 8px 0 4px; font-size: 32px; }
.login-card-copy { margin: 0 0 26px; color: #9297a8; font-size: 14px; }

.login-tabs {
  height: 45px;
  margin-bottom: 22px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 4px;
  border-radius: 7px;
  background: #1b1d28;
}

.login-tabs button {
  border: 0;
  border-radius: 5px;
  color: #858b9e;
  background: transparent;
  font-weight: 900;
  cursor: pointer;
}

.login-tabs button.active { color: #fff3d6; background: linear-gradient(180deg, #424658, #343746); box-shadow: inset 0 -2px var(--login-gold); }
.login-error { margin-bottom: 16px; padding: 10px 12px; color: #ffd2c8; border: 1px solid rgba(255, 89, 52, 0.35); border-radius: 6px; background: rgba(153, 37, 23, 0.2); font-size: 13px; }

.login-field { display: block; margin-top: 16px; }
.login-field > span { display: block; margin-bottom: 7px; color: #b8bdca; font-size: 13px; font-weight: 700; }
.login-field input {
  width: 100%;
  height: 48px;
  padding: 0 14px;
  box-sizing: border-box;
  border: 1px solid #414455;
  border-radius: 6px;
  outline: 0;
  color: #fff;
  background: #1c1e29;
  font: inherit;
}
.login-field input:focus { border-color: #bc7d23; box-shadow: 0 0 0 3px rgba(255, 166, 34, 0.08); }
.login-password, .login-code { position: relative; display: grid; grid-template-columns: minmax(0, 1fr) auto; }
.login-password input, .login-code input { grid-column: 1 / -1; padding-right: 88px; }
.login-password button, .login-code button { height: 36px; position: absolute; right: 6px; top: 6px; padding: 0 12px; border: 0; border-left: 1px solid #444859; color: var(--login-gold); background: transparent; font-weight: 800; cursor: pointer; }
.login-code button:disabled { color: #727789; cursor: default; }

.login-options { margin: 16px 0; display: flex; justify-content: space-between; align-items: center; color: #aeb3c1; font-size: 13px; }
.login-options label, .login-consent label { display: flex; align-items: center; gap: 7px; }
.login-options input, .login-consent input { accent-color: #f39820; }
.login-options button, .login-consent button, .login-register button { padding: 0; border: 0; color: var(--login-gold); background: none; font: inherit; cursor: pointer; }
.login-consent { display: grid; gap: 8px; color: #858a9a; font-size: 12px; line-height: 1.4; }

.login-submit {
  width: 100%;
  height: 50px;
  margin-top: 23px;
  border: 0;
  border-radius: 6px;
  color: #fff;
  background: linear-gradient(180deg, #ff9518, #f05b00);
  box-shadow: 0 10px 26px rgba(255, 104, 7, 0.3);
  font-size: 16px;
  font-weight: 1000;
  cursor: pointer;
}
.login-submit:disabled { opacity: 0.6; cursor: wait; }
.login-submit i { display: inline-block; width: 13px; height: 13px; margin-left: 8px; background: url("../assets/pizzaking/pizza-token.svg") center / contain no-repeat; }
.login-register { margin-top: 18px; color: #9297a8; text-align: center; font-size: 13px; }

.captcha-mask { position: fixed; inset: 0; z-index: 20; display: grid; place-items: center; padding: 18px; background: rgba(7, 8, 13, 0.82); backdrop-filter: blur(8px); }
.captcha-card { width: min(390px, 100%); padding: 30px; position: relative; box-sizing: border-box; border: 1px solid #4a4d5d; border-radius: 10px; background: #272a37; box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55); }
.captcha-close { position: absolute; right: 14px; top: 10px; border: 0; color: #999eae; background: none; font-size: 25px; cursor: pointer; }
.captcha-card > span { color: var(--login-gold); font-size: 11px; font-weight: 1000; letter-spacing: 0.16em; }
.captcha-card h3 { margin: 7px 0; font-size: 23px; }
.captcha-card p { margin: 0 0 18px; color: #9297a8; font-size: 13px; }
.captcha-row { display: grid; grid-template-columns: minmax(0, 1fr) 128px; gap: 10px; }
.captcha-row > input { min-width: 0; height: 46px; padding: 0 12px; border: 1px solid #444859; border-radius: 5px; outline: 0; color: #fff; background: #1b1d27; font-size: 19px; letter-spacing: 0.15em; }
.captcha-image { height: 46px; overflow: hidden; padding: 0; border: 0; border-radius: 5px; background: #eee; cursor: pointer; }
.captcha-image > * { width: 100% !important; height: 100% !important; }
.captcha-confirm { width: 100%; height: 44px; margin-top: 18px; border: 0; border-radius: 5px; color: #211508; background: linear-gradient(180deg, #ffd06a, #f39a1d); font-weight: 1000; cursor: pointer; }

.login-policy-mask { position: fixed; inset: 0; z-index: 30; overflow-y: auto; padding: 28px; box-sizing: border-box; background: rgba(7, 8, 13, 0.9); backdrop-filter: blur(10px); }
.login-policy-card { width: min(900px, 100%); margin: 0 auto; overflow: hidden; border: 1px solid #444859; border-radius: 12px; background: #242733; box-shadow: 0 35px 90px rgba(0, 0, 0, 0.6); }
.login-policy-card > header { padding: 24px 28px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255, 255, 255, 0.08); background: linear-gradient(135deg, rgba(255, 142, 24, 0.12), transparent 55%); }
.login-policy-card > header span { color: var(--login-gold); font-size: 11px; font-weight: 1000; letter-spacing: 0.16em; }
.login-policy-card > header h3 { margin: 5px 0 0; font-size: 28px; }
.login-policy-card > header button { border: 0; color: #a9aebb; background: none; font-size: 30px; cursor: pointer; }
.login-policy-content { padding: 26px 28px 32px; }
.login-policy-content > p { margin: 0 0 20px; color: #aeb4c4; line-height: 1.8; }
.login-policy-content article { padding: 18px 0; display: grid; grid-template-columns: 34px 1fr; gap: 14px; border-top: 1px solid rgba(255, 255, 255, 0.07); }
.login-policy-content article > b { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 50%; color: #1b1408; background: linear-gradient(180deg, #ffd06a, #f39a1d); }
.login-policy-content h4 { margin: 2px 0 8px; font-size: 18px; }
.login-policy-content article p { margin: 0; color: #9ba2b4; line-height: 1.75; }

@media (max-width: 980px) {
  .login-stage { width: min(520px, calc(100% - 32px)); grid-template-columns: 1fr; gap: 0; }
  .login-showcase { display: none; }
}

@media (max-width: 600px) {
  .login-page { min-height: 100svh; overflow: auto; }
  .login-header { height: 68px; padding: 0 15px; }
  .login-brand img { width: 176px; }
  .login-back { height: 36px; padding: 0 10px; font-size: 12px; }
  .login-stage { width: 100%; min-height: calc(100svh - 68px); padding: 18px 12px 28px; align-items: start; }
  .login-card { padding: 28px 20px 24px; border-radius: 9px; }
  .login-card::before { left: 20px; right: 20px; }
  .login-card h2 { font-size: 27px; }
  .login-tabs { margin-bottom: 18px; }
  .login-field input { height: 46px; }
  .login-submit { height: 48px; }
  .captcha-card { padding: 28px 20px 22px; }
  .login-policy-mask { padding: 12px; }
  .login-policy-card > header, .login-policy-content { padding-left: 18px; padding-right: 18px; }
}
</style>
